import {
  createExpandingFilm
} from "./bb-chunk-M45N2KQU.js";

// runtime/source/site/team/why-introduction.js
var clamp = (n) => Math.max(0, Math.min(1, n));
var phase = (p, start, end) => clamp((p - start) / (end - start));
var smooth = (t) => t * t * (3 - 2 * t);
var PHOTO_END = 1.3;
var WHY_READING = {
  closingStart: 0.2,
  closingVisible: 0.45,
  highlightEnd: PHOTO_END,
  photoReadingEnd: 0.35,
  photoEnd: 0.8,
  travel: PHOTO_END + 0.15
};
function whyHighlight(progress, index, count) {
  return 0.48 + 0.52 * smooth(clamp(progress * (count + 1) - index));
}
function whyClosingHighlight(progress, index, count) {
  return 0.48 + 0.52 * smooth(clamp(progress * count - index));
}
var WHY_MODES = Object.freeze(["flow", "pin"]);
var whyMode = (search, attr) => {
  const q = new URLSearchParams(search).get("why");
  return WHY_MODES.includes(q) ? q : WHY_MODES.includes(attr) ? attr : "flow";
};
function whyFlowGeometry({ viewport, gap, closingHeight, compact = false, naturalWidth = 0 }) {
  const frame = compact ? naturalWidth / 1.3 : Math.max(240, viewport - 3 * gap - closingHeight);
  const hold = Math.max(0, Math.min(0.35 * viewport, viewport - 2 * gap - frame));
  return { frame, hold };
}
var whyFlowOpen = (trackTop, viewport, gap) => smooth(clamp((viewport - trackTop) / Math.max(1, viewport - gap)));
function whyFlowInk(runTop, runHeight, viewport) {
  const start = Math.min(0.86 * viewport, viewport - runHeight - 16), end = 0.5 * viewport;
  return clamp((start - runTop) / Math.max(1, start - end));
}
function whyPhotoStart({ storyTop, viewport, stageHeight, photoTop, photoBottom }) {
  const visible = storyTop + photoTop < viewport && storyTop + photoBottom > 0;
  return visible ? Math.min(-0.25, -storyTop / Math.max(1, stageHeight)) : -0.25;
}
function whyPhotoOpen(distance, readingOpen = 0.28, readingStart = -0.25) {
  const readingEnd = WHY_READING.photoReadingEnd, readingLength = readingEnd - readingStart;
  if (distance <= readingEnd) return readingOpen * phase(distance, readingStart, readingEnd);
  const t = phase(distance, readingEnd, WHY_READING.photoEnd), t2 = t * t, t3 = t2 * t;
  const tangent = readingOpen / readingLength * (WHY_READING.photoEnd - readingEnd);
  return (2 * t3 - 3 * t2 + 1) * readingOpen + (t3 - 2 * t2 + t) * tangent - 2 * t3 + 3 * t2;
}
function whyReadingPose(distance, readingOpen = 0.28, readingStart = -0.25) {
  const open = whyPhotoOpen(distance, readingOpen, readingStart);
  const closing = phase(distance, WHY_READING.closingStart, WHY_READING.closingVisible);
  return {
    textY: 0,
    textOpacity: 1,
    readingLift: smooth(phase(distance, 0, 0.25)),
    sharedLayout: smooth(phase(distance, 0, 0.25)),
    photoY: 16 * (1 - open),
    photoScale: 0.66 + 0.34 * open,
    photoInset: 18 * (1 - open),
    photoOpacity: smooth(phase(distance, -0.25, 0.15)),
    closingY: 6 * (1 - smooth(closing)),
    closingOpacity: closing,
    closingHighlight: phase(distance, WHY_READING.closingVisible, WHY_READING.highlightEnd)
  };
}
function wordRun(node) {
  const original = Array.from(node.childNodes, (child) => child.cloneNode(true)), words = [];
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT), texts = [];
  while (walker.nextNode()) texts.push(walker.currentNode);
  for (const text of texts) {
    const fragment = document.createDocumentFragment();
    for (const part of text.textContent.split(/(\s+)/)) {
      if (!part) continue;
      if (/^\s+$/.test(part)) {
        fragment.append(document.createTextNode(part));
        continue;
      }
      const word = document.createElement("span");
      word.className = "bb-why-word";
      word.textContent = part;
      fragment.append(word);
      words.push(word);
    }
    text.replaceWith(fragment);
  }
  return { node, words, original, top: 0, height: 0, active: false, last: null, peak: 0 };
}
function createCinematicIntroduction(story) {
  const abort = new AbortController(), motion = matchMedia("(prefers-reduced-motion: reduce)");
  const film = story.querySelector("[data-expanding-film]");
  const disposeFilm = createExpandingFilm(film, { contentFrame: document.querySelector(".bb-team-hero .bb-header") });
  const runs = [".bb-team-belief h2", ".bb-team-focus"].map((selector2) => {
    const node = story.querySelector(selector2);
    return node ? wordRun(node) : null;
  });
  let raf = 0, disposed = false;
  function render() {
    raf = 0;
    if (disposed) return;
    const allowed = !motion.matches && document.documentElement.dataset.motion !== "off";
    const gap = parseFloat(getComputedStyle(story).getPropertyValue("--why-gap")) || 56;
    runs.forEach((run, index) => {
      if (!run) return;
      if (!allowed) {
        run.words.forEach((word) => word.style.removeProperty("opacity"));
        return;
      }
      const rect = run.node.getBoundingClientRect();
      const start = Math.min(0.86 * innerHeight, innerHeight - rect.height - 16);
      const release = Math.max(0.5 * innerHeight, 2 * gap + film.getBoundingClientRect().height);
      const end = index && start - release >= 0.2 * innerHeight ? release : 0.5 * innerHeight;
      run.peak = Math.max(run.peak, clamp((start - rect.top) / Math.max(1, start - end)));
      run.words.forEach((word, i) => word.style.opacity = String((index ? whyClosingHighlight : whyHighlight)(run.peak, i, run.words.length)));
    });
  }
  const schedule = () => {
    if (!raf) raf = requestAnimationFrame(render);
  };
  addEventListener("scroll", schedule, { passive: true, signal: abort.signal });
  addEventListener("resize", schedule, { passive: true, signal: abort.signal });
  motion.addEventListener("change", schedule, { signal: abort.signal });
  render();
  document.fonts?.ready.then(() => {
    if (!disposed) schedule();
  });
  return () => {
    disposed = true;
    abort.abort();
    cancelAnimationFrame(raf);
    disposeFilm();
    runs.forEach((run) => run?.node.replaceChildren(...run.original));
  };
}
function createWhyIntroduction(story) {
  if (story.dataset.whyFilm === "cinematic") return createCinematicIntroduction(story);
  const abort = new AbortController(), motion = matchMedia("(prefers-reduced-motion: reduce)");
  const belief = story.querySelector(".bb-team-belief"), photo = story.querySelector(".bb-team-travel"), closing = story.querySelector(".bb-team-focus");
  const stage = story.querySelector(".bb-why-stage"), heading = belief.querySelector("h2");
  const track = story.querySelector(".bb-why-photo-track") || photo.parentElement;
  const mode = whyMode(location.search, story.dataset.whyMode);
  const runs = [wordRun(heading), wordRun(closing)];
  let enabled = false, flow = false, gapPx = 56, raf = 0, disposed = false, readingOpen = 0.28, readingShift = 0, readingStart = -0.25;
  let photoTop = 0, photoHeight = 0, beliefBottom = 0, closingTop = 0;
  const gap = 24;
  function paint(run, progress, isClosing = false) {
    if (progress === run.last) return;
    run.last = progress;
    run.words.forEach((word, index) => {
      const value = (isClosing ? whyClosingHighlight : whyHighlight)(progress, index, run.words.length);
      const opacity = String(isClosing && value < 1 ? Math.min(0.999, +value.toFixed(3)) : +value.toFixed(3));
      if (word.style.opacity !== opacity) word.style.opacity = opacity;
    });
  }
  function photoPlacement(pose, height) {
    const readingBottom = beliefBottom + readingShift * pose.readingLift + pose.textY * height / 100;
    const center = (beliefBottom + readingShift + closingTop) / 2;
    const available = 2 * Math.max(0, Math.min(center - readingBottom - gap, closingTop - gap - center));
    const insetY = Math.max(pose.photoInset, 50 * (1 - available / (photoHeight * pose.photoScale)));
    return { y: center - photoTop - photoHeight / 2, insetY };
  }
  function render() {
    raf = 0;
    if (disposed) return;
    const box = story.getBoundingClientRect(), height = Math.max(1, stage.clientHeight), distance = -box.top / height;
    if (flow) {
      runs.forEach((run, index) => {
        if (!run.active) return;
        run.peak = Math.max(run.peak, whyFlowInk(box.top + run.top, run.height, innerHeight));
        paint(run, run.peak, index === 1);
      });
      const open = whyFlowOpen(track.getBoundingClientRect().top, innerHeight, gapPx);
      photo.style.transform = `scale(${(0.66 + 0.34 * open).toFixed(4)})`;
      photo.style.clipPath = `inset(0 ${(18 * (1 - open)).toFixed(2)}%)`;
      return;
    }
    const pose = whyReadingPose(distance, readingOpen, readingStart);
    runs.forEach((run, index) => {
      if (!run.active) return;
      const view = enabled ? height : innerHeight;
      const start = Math.max(96, Math.min(view * 0.66, view - run.height - 32));
      const end = Math.min(start - 80, Math.max(104, view * 0.24));
      paint(run, enabled ? index ? pose.closingHighlight : phase(distance, -0.35, 0.15) : clamp((start - (box.top + run.top)) / (start - end)), index === 1);
    });
    if (!enabled) return;
    const clearance = photoPlacement(pose, height);
    belief.style.transform = `translateY(${readingShift * pose.readingLift + pose.textY * height / 100}px)`;
    belief.style.opacity = pose.textOpacity;
    photo.style.transform = `translateY(${clearance.y}px) scale(${pose.photoScale})`;
    photo.style.clipPath = `inset(${clearance.insetY}% ${pose.photoInset}%)`;
    photo.style.opacity = pose.photoOpacity;
    closing.style.transform = `translateY(${pose.closingY * height / 100}px)`;
    closing.style.opacity = pose.closingOpacity;
  }
  function reset() {
    for (const node of [belief, photo, closing]) for (const prop of ["transform", "opacity", "clip-path"]) node.style.removeProperty(prop);
  }
  function configure() {
    readingStart = -0.25;
    const allowed = !motion.matches && document.documentElement.dataset.motion !== "off";
    runs.forEach((run) => run.last = null);
    reset();
    story.classList.remove("is-scroll-story");
    story.classList.remove("is-flow-story");
    for (const name of ["--why-frame", "--why-hold"]) story.style.removeProperty(name);
    flow = allowed && mode === "flow";
    enabled = !flow && allowed && innerWidth >= 350 && innerHeight >= 600;
    if (flow) {
      gapPx = parseFloat(getComputedStyle(story).getPropertyValue("--why-gap")) || 56;
      const compact = innerWidth <= 760;
      const g = whyFlowGeometry({ viewport: innerHeight, gap: gapPx, closingHeight: closing.offsetHeight, compact, naturalWidth: photo.getBoundingClientRect().width });
      story.style.setProperty("--why-frame", `${Math.round(g.frame)}px`);
      story.style.setProperty("--why-hold", `${Math.round(g.hold)}px`);
      story.classList.add("is-flow-story");
    }
    story.classList.toggle("is-scroll-story", enabled);
    if (enabled) {
      const s2 = stage.getBoundingClientRect(), b = heading.getBoundingClientRect(), p = photo.getBoundingClientRect(), c = closing.getBoundingClientRect();
      const label = belief.querySelector(".bb-label").getBoundingClientRect();
      const height = stage.clientHeight, readingTop = innerWidth < 800 ? 24 : Math.min(64, height * 0.06);
      readingShift = Math.min(0, readingTop - (label.top - s2.top));
      photoTop = p.top - s2.top;
      photoHeight = p.height;
      beliefBottom = b.bottom - s2.top;
      closingTop = c.top - s2.top;
      readingOpen = 0.28;
      const fits = () => {
        const pose = whyReadingPose(WHY_READING.photoReadingEnd, readingOpen);
        const visibleHeight = photoHeight * pose.photoScale * (1 - pose.photoInset / 50);
        return beliefBottom + readingShift + gap + visibleHeight + gap <= closingTop;
      };
      while (!fits() && readingOpen > 0.1) readingOpen = +(readingOpen - 0.01).toFixed(2);
      const initialFit = fits();
      const navHeight = document.querySelector(".bb-scroll-header")?.offsetHeight || 0;
      readingShift = Math.max(readingTop, navHeight + gap) - (label.top - s2.top);
      const photoRoom = closingTop - (beliefBottom + readingShift) - gap * 2;
      if (!initialFit || photoRoom < 96) {
        enabled = false;
        story.classList.remove("is-scroll-story");
      }
      if (enabled) {
        const closed = whyReadingPose(-0.25, readingOpen), placement = photoPlacement(closed, height);
        const center = photoTop + placement.y + photoHeight / 2;
        const half = photoHeight * closed.photoScale * (0.5 - placement.insetY / 100);
        readingStart = whyPhotoStart({
          storyTop: story.getBoundingClientRect().top + scrollY,
          viewport: innerHeight,
          stageHeight: height,
          photoTop: center - half,
          photoBottom: center + half
        });
      }
    }
    const s = story.getBoundingClientRect();
    runs.forEach((run) => {
      const r = run.node.getBoundingClientRect();
      run.top = r.top - s.top;
      run.height = r.height;
      run.active = allowed && (enabled || run.height < innerHeight - 208);
      if (!run.active) run.words.forEach((word) => word.style.removeProperty("opacity"));
    });
    render();
  }
  const schedule = () => {
    if ((enabled || runs.some((run) => run.active)) && !raf) raf = requestAnimationFrame(render);
  };
  addEventListener("scroll", schedule, { passive: true, signal: abort.signal });
  addEventListener("resize", configure, { passive: true, signal: abort.signal });
  motion.addEventListener("change", configure, { signal: abort.signal });
  configure();
  document.fonts?.ready.then(() => {
    if (!disposed) configure();
  });
  return () => {
    disposed = true;
    abort.abort();
    cancelAnimationFrame(raf);
    story.classList.remove("is-scroll-story");
    story.classList.remove("is-flow-story");
    reset();
    runs.forEach((run) => run.node.replaceChildren(...run.original));
  };
}

// runtime/source/site/team/team-motion.js
var PARTNER_SEQUENCE = { wipeSpan: 0.6, panelWidth: 520, top: 96, bottom: 32, grid: 1, photoDepth: 0.018, photoTravel: 0.05, maxPhotoTravel: 56, openingSpan: 0.75 };
var PARTNER_COPY = { character: 143, characterStagger: 7, indexDelay: 27, indexStagger: 11, rule: 289, ruleDelay: 14, lowerRuleDelay: 177, name: 238, nameDelay: 48, wordStagger: 24, nameTravel: 24, bio: 218, bioDelay: 190, lineStagger: 18, maxLineDelay: 88, action: 116, actionDelay: 381, replayCooldown: 500, ease: "cubic-bezier(.19,1,.22,1)" };
var clamp2 = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
function partnerStageLayout({ width, height, panelHeight, gutter = 24, navHeight = 80 }) {
  const c = PARTNER_SEQUENCE;
  const panelWidth = Math.min(c.panelWidth, Math.max(0, width - 2 * gutter));
  const panelTop = Math.max(c.top, Math.ceil((navHeight + 16) / c.grid) * c.grid, Math.floor((height - panelHeight) / 2 / c.grid) * c.grid);
  return { fits: panelWidth > 0 && panelHeight > 0 && panelTop + panelHeight <= height - c.bottom, panelTop, panelWidth };
}
function partnerSequencePose(phase2, count = 3, viewportHeight = 900) {
  phase2 = clamp2(phase2, 0, count);
  const half = PARTNER_SEQUENCE.wipeSpan / 2;
  const wipes = Array.from({ length: count }, (_, i) => i === 0 ? 1 : clamp2((phase2 - (i - half)) / PARTNER_SEQUENCE.wipeSpan));
  const active = clamp2(Math.floor(phase2), 0, count - 1);
  const copy = Array.from({ length: count }, (_, i) => i === active ? 1 : 0);
  const height = Math.max(1, viewportHeight), travel = Math.min(PARTNER_SEQUENCE.maxPhotoTravel, height * PARTNER_SEQUENCE.photoTravel);
  const photoY = wipes.map((wipe, i) => i === 0 || wipe === 1 ? 0 : -travel * (1 - wipe));
  const depth = wipes.map((wipe, i) => (1 + (2 * Math.abs(photoY[i]) + (i ? 2 * (1 - wipe) : 0)) / height) * (1 + PARTNER_SEQUENCE.photoDepth * (wipes[i + 1] || 0)));
  return { wipes, copy, active, depth, photoY };
}
var PARTNER_OPENING = { widthRatio: 0.68, maxWidthRatio: 0.96, heightRatio: 0.7, maxHeight: 560, exposedWidth: 224, margin: 24, imageWidth: 1920, imageHeight: 720, faceX: 940, faceHalfWidth: 120, restPosition: 0.28 };
function partnerOpeningPose(phase2, width, height, panelRight = PARTNER_SEQUENCE.panelWidth + 24) {
  const c = PARTNER_OPENING;
  const progress = clamp2(phase2 / PARTNER_SEQUENCE.openingSpan);
  const eased = progress * progress * (3 - 2 * progress);
  const startWidth = Math.min(width * c.maxWidthRatio, Math.max(width * c.widthRatio, panelRight + c.exposedWidth));
  const startHeight = Math.min(height * c.heightRatio, c.maxHeight);
  const photoWidth = startWidth + (width - startWidth) * eased, photoHeight = startHeight + (height - startHeight) * eased;
  const scale = Math.max(photoWidth / c.imageWidth, photoHeight / c.imageHeight), overhang = c.imageWidth * scale - photoWidth;
  const left = panelRight + c.margin + c.faceHalfWidth * scale, right = photoWidth - c.margin - c.faceHalfWidth * scale;
  const face = left <= right ? clamp2(c.faceX * scale - overhang * c.restPosition, left, right) : (panelRight + photoWidth) / 2;
  const position = overhang > 0 ? clamp2((c.faceX * scale - face) / overhang) : c.restPosition;
  return { width: photoWidth, height: photoHeight, position: position * 100, mainPhase: phase2 - PARTNER_SEQUENCE.openingSpan };
}
var PARTNER_MODES = Object.freeze(["sections", "pin"]);
var partnerMode = (search, attr) => {
  const q = new URLSearchParams(search).get("partners");
  return PARTNER_MODES.includes(q) ? q : PARTNER_MODES.includes(attr) ? attr : "sections";
};

// runtime/source/site/team/team-transitions.js
function createPartnerTransitions(story) {
  const stage = story?.querySelector(".bb-partner-stage");
  const sections = [...stage?.querySelectorAll(".bb-partner-feature") || []];
  if (!stage || sections.length < 2 || !window.CSS?.supports("position", "sticky")) return () => {
  };
  const panels = sections.map((section) => section.querySelector(".bb-partner-panel"));
  const preference = matchMedia("(prefers-reduced-motion: reduce)");
  const lifetime2 = new AbortController();
  const on = (target, event, fn, options = {}) => target.addEventListener(event, fn, { ...options, signal: lifetime2.signal });
  const mode = partnerMode(location.search, story.dataset.partnerMode);
  const opening = mode === "pin" && document.documentElement.dataset.teamOpening === "inset";
  const props = ["--partner-panel-width", "--partner-panel-height", "--partner-panel-top", "--partner-viewport-height", "--partner-track-height", "--partner-opening-width", "--partner-opening-height", "--partner-opening-position"];
  let pinned = false, height = 0, panelRight = 0, frame = 0, resizeFrame = 0, focusIndex = null, observer = null, current = -1, entered = false, ready = !document.fonts;
  const originals = /* @__PURE__ */ new Map(), copyGroups = /* @__PURE__ */ new Map(), decorations = [], motions = /* @__PURE__ */ new Set(), lastReveal = /* @__PURE__ */ new Map();
  const viewportHeight = () => Math.max(1, Math.floor(Math.min(innerHeight, window.visualViewport?.height || innerHeight)));
  function settleCopy() {
    for (const motion of motions) motion.cancel();
    motions.clear();
  }
  function plain(element) {
    return element?.childNodes.length === 1 && element.firstChild.nodeType === Node.TEXT_NODE;
  }
  function measuredLines(element) {
    if (!plain(element)) return [];
    const text = element.textContent, node = element.firstChild, range = document.createRange();
    const parts = [];
    let top = null, start = 0;
    for (const word of text.matchAll(/\S+/g)) {
      range.setStart(node, word.index);
      range.setEnd(node, word.index + word[0].length);
      const next = range.getBoundingClientRect().top;
      if (top !== null && Math.abs(next - top) > 2) {
        parts.push(text.slice(start, word.index));
        start = word.index;
      }
      top = next;
    }
    parts.push(text.slice(start));
    originals.set(element, element.innerHTML);
    return parts;
  }
  function masked(text, type) {
    const mask = document.createElement("span"), content = document.createElement("span");
    mask.className = `bb-partner-${type}-mask`;
    content.className = `bb-partner-${type}`;
    content.textContent = text;
    mask.append(content);
    return { mask, content };
  }
  function characters(element) {
    if (!plain(element)) return [];
    originals.set(element, element.innerHTML);
    const fragment = document.createDocumentFragment(), items = [];
    for (const char of Array.from(element.textContent)) {
      const { mask, content } = masked(char, "char");
      fragment.append(mask);
      items.push(content);
    }
    element.replaceChildren(fragment);
    return items;
  }
  function prepareCopy() {
    panels.forEach((panel) => {
      const group = { role: [], index: [], words: [], lines: [], rules: [] };
      const labels = panel.querySelectorAll(".bb-label>span");
      group.role = characters(labels[0]);
      group.index = characters(labels[1]);
      const heading = panel.querySelector("h2"), parts = measuredLines(heading);
      if (parts.length) {
        const fragment = document.createDocumentFragment();
        for (const text of parts) {
          const row = document.createElement("span");
          row.className = "bb-partner-heading-row";
          for (const token of text.match(/\S+|\s+/g) || []) {
            if (/^\s+$/.test(token)) {
              row.append(document.createTextNode(token));
              continue;
            }
            const { mask, content } = masked(token, "word");
            row.append(mask);
            group.words.push(content);
          }
          fragment.append(row);
        }
        heading.replaceChildren(fragment);
      }
      for (const paragraph of panel.querySelectorAll(".bb-partner-panel-copy>p")) {
        const lines = measuredLines(paragraph);
        if (!lines.length) continue;
        const fragment = document.createDocumentFragment();
        for (const text of lines) {
          const { mask, content } = masked(text, "bio-line");
          mask.className = "bb-partner-bio-mask";
          fragment.append(mask);
          group.lines.push(content);
        }
        paragraph.replaceChildren(fragment);
      }
      const copy = panel.querySelector(".bb-partner-panel-copy");
      for (const position of ["top", "bottom"]) {
        const rule = document.createElement("span");
        rule.className = `bb-partner-rule bb-partner-rule--${position}`;
        rule.setAttribute("aria-hidden", "true");
        copy.append(rule);
        decorations.push(rule);
        group.rules.push(rule);
      }
      copyGroups.set(panel, group);
    });
  }
  function revealCopy(index) {
    const panel = panels[index], c = PARTNER_COPY, group = copyGroups.get(panel), now = performance.now();
    if (!group || now - (lastReveal.get(panel) ?? -Infinity) < c.replayCooldown) return;
    lastReveal.set(panel, now);
    const animate = (element, frames, duration, delay = 0) => {
      if (!element?.animate) return;
      const motion = element.animate(frames, { duration, delay, easing: c.ease, fill: "backwards" });
      motions.add(motion);
      motion.onfinish = () => {
        motion.cancel();
        motions.delete(motion);
      };
    };
    const chars = [{ opacity: 0, transform: "translateY(12px)" }, { opacity: 1, transform: "translateY(0px)" }];
    group.role.forEach((char, i) => animate(char, chars, c.character, i * c.characterStagger));
    group.index.forEach((char, i) => animate(char, chars, c.character, c.indexDelay + i * c.indexStagger));
    group.rules.forEach((rule, i) => animate(rule, [{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }], c.rule, i ? c.lowerRuleDelay : c.ruleDelay));
    group.words.forEach((word, i) => animate(word, [{ opacity: 0, transform: `translateY(${c.nameTravel}px)` }, { opacity: 1, transform: "translateY(0px)" }], c.name, c.nameDelay + i * c.wordStagger));
    group.lines.forEach((line, i) => animate(line, [{ transform: "translateY(calc(100% + 1px))" }, { transform: "translateY(0px)" }], c.bio, c.bioDelay + Math.min(i * c.lineStagger, c.maxLineDelay)));
    animate(panel.querySelector("a"), [{ opacity: 0, transform: "translateY(12px)" }, { opacity: 1, transform: "translateY(0px)" }], c.action, c.actionDelay);
  }
  function clear() {
    settleCopy();
    current = -1;
    for (const [element, html] of originals) element.innerHTML = html;
    originals.clear();
    copyGroups.clear();
    for (const rule of decorations) rule.remove();
    decorations.length = 0;
    pinned = false;
    story.classList.remove("is-pinned");
    story.removeAttribute("data-partner-measuring");
    story.removeAttribute("data-partner-opening");
    story.style.removeProperty("height");
    for (const name of props) story.style.removeProperty(name);
    for (const section of sections) {
      section.removeAttribute("data-partner-active");
      for (const name of ["--partner-layer", "--partner-wipe", "--partner-copy", "--partner-depth", "--partner-photo-y"]) section.style.removeProperty(name);
    }
  }
  function update(animate = true) {
    frame = 0;
    if (!pinned) return;
    let phase2 = -story.getBoundingClientRect().top / height;
    if (opening) {
      const intro = partnerOpeningPose(phase2, document.documentElement.clientWidth, height, panelRight);
      story.style.setProperty("--partner-opening-width", `${intro.width.toFixed(3)}px`);
      story.style.setProperty("--partner-opening-height", `${intro.height.toFixed(3)}px`);
      story.style.setProperty("--partner-opening-position", `${intro.position.toFixed(4)}%`);
      phase2 = intro.mainPhase;
    }
    const pose = partnerSequencePose(phase2, sections.length, height);
    if (focusIndex !== null) {
      pose.active = focusIndex;
      pose.wipes = sections.map((_, i) => i <= focusIndex ? 1 : 0);
      pose.copy = sections.map((_, i) => i === focusIndex ? 1 : 0);
      pose.depth = sections.map(() => 1);
      pose.photoY = sections.map(() => 0);
    }
    sections.forEach((section, i) => {
      section.style.setProperty("--partner-wipe", `${((1 - pose.wipes[i]) * 100).toFixed(4)}%`);
      section.style.setProperty("--partner-copy", pose.copy[i].toFixed(4));
      section.style.setProperty("--partner-depth", pose.depth[i].toFixed(6));
      section.style.setProperty("--partner-photo-y", `${pose.photoY[i].toFixed(3)}px`);
      section.toggleAttribute("data-partner-active", pose.active === i);
    });
    const rect = panels[pose.active].getBoundingClientRect();
    const visible = rect.top < height * 0.82 && rect.bottom > 0;
    const changed = pose.active !== current;
    if (changed || focusIndex !== null) settleCopy();
    if (visible && animate && ready) {
      if (focusIndex === null && (changed || !entered)) revealCopy(pose.active);
      else if (focusIndex !== null) lastReveal.set(panels[pose.active], performance.now());
      entered = true;
    }
    current = pose.active;
  }
  function schedule() {
    if (pinned && !frame) frame = requestAnimationFrame(() => update());
  }
  function releaseFocus() {
    if (focusIndex === null) return;
    focusIndex = null;
    schedule();
  }
  function configure() {
    const reservedHeight = pinned ? story.getBoundingClientRect().height : 0;
    clear();
    if (mode !== "pin") return;
    if (preference.matches || document.documentElement.clientWidth <= 760 || document.documentElement.dataset.motion === "off") return;
    if (reservedHeight) story.style.setProperty("height", `${reservedHeight}px`);
    height = viewportHeight();
    const width = document.documentElement.clientWidth;
    const gutter = parseFloat(getComputedStyle(sections[0]).paddingLeft) || 24;
    const navHeight = document.querySelector(".bb-scroll-header")?.getBoundingClientRect().height || 80;
    const panelWidth = Math.min(PARTNER_SEQUENCE.panelWidth, Math.max(0, width - gutter * 2));
    panelRight = gutter + panelWidth;
    story.style.setProperty("--partner-panel-width", `${panelWidth}px`);
    story.setAttribute("data-partner-measuring", "");
    const panelHeight = Math.ceil(Math.max(...panels.map((panel) => panel.getBoundingClientRect().height)) / PARTNER_SEQUENCE.grid) * PARTNER_SEQUENCE.grid;
    const layout = partnerStageLayout({ width, height, panelHeight, gutter, navHeight });
    story.removeAttribute("data-partner-measuring");
    if (!layout.fits) {
      clear();
      return;
    }
    story.style.setProperty("--partner-panel-height", `${panelHeight}px`);
    story.style.setProperty("--partner-panel-top", `${layout.panelTop}px`);
    story.style.setProperty("--partner-viewport-height", `${height}px`);
    story.style.setProperty("--partner-track-height", `${height * (sections.length + 1 + (opening ? PARTNER_SEQUENCE.openingSpan : 0))}px`);
    if (opening) story.setAttribute("data-partner-opening", "inset");
    sections.forEach((section, i) => section.style.setProperty("--partner-layer", String(i + 1)));
    story.classList.add("is-pinned");
    story.style.removeProperty("height");
    pinned = true;
    prepareCopy();
    update(false);
  }
  function scheduleLayout() {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(configure);
  }
  function anchor() {
    const index = sections.findIndex((section) => "#" + section.id === location.hash);
    if (index >= 0) {
      focusIndex = index;
      update();
    }
  }
  on(window, "scroll", schedule, { passive: true });
  on(window, "resize", scheduleLayout, { passive: true });
  on(window, "pageshow", scheduleLayout);
  on(preference, "change", configure);
  if (window.visualViewport) on(window.visualViewport, "resize", scheduleLayout, { passive: true });
  on(story, "focusin", (event) => {
    const index = panels.findIndex((panel) => panel.contains(event.target));
    if (index >= 0) {
      focusIndex = index;
      update();
    }
  });
  on(story, "focusout", (event) => {
    if (!story.contains(event.relatedTarget)) releaseFocus();
  });
  on(window, "pointerdown", releaseFocus, { passive: true });
  on(window, "wheel", releaseFocus, { passive: true });
  on(window, "touchstart", releaseFocus, { passive: true });
  on(window, "keydown", (event) => {
    if (["ArrowDown", "ArrowUp", "PageDown", "PageUp", "Home", "End", " "].includes(event.key)) releaseFocus();
  });
  on(window, "hashchange", anchor);
  configure();
  anchor();
  if ("ResizeObserver" in window) {
    const sizes = /* @__PURE__ */ new WeakMap();
    observer = new ResizeObserver((entries) => {
      let changed = false;
      for (const { target, contentRect } of entries) {
        const size = `${contentRect.width},${contentRect.height}`;
        if (sizes.has(target) && sizes.get(target) !== size) changed = true;
        sizes.set(target, size);
      }
      if (changed) scheduleLayout();
    });
    for (const panel of panels) for (const child of panel.children) observer.observe(child);
  }
  if (document.fonts) document.fonts.ready.then(() => {
    if (!lifetime2.signal.aborted) {
      ready = true;
      configure();
      schedule();
    }
  });
  else schedule();
  return () => {
    lifetime2.abort();
    observer?.disconnect();
    cancelAnimationFrame(frame);
    cancelAnimationFrame(resizeFrame);
    clear();
  };
}

// runtime/source/site/team/team.js
var query = new URLSearchParams(location.search);
if (query.get("motion") === "off") document.documentElement.dataset.motion = "off";
if (query.get("scroll") === "fallback") document.documentElement.dataset.scrollFallback = "";
if (query.get("opening") === "inset") document.documentElement.dataset.teamOpening = "inset";
if (document.documentElement.dataset.teamOpening === "inset" && !document.querySelector(".bb-team-review-variant")) {
  const note = document.createElement("aside");
  note.className = "bb-team-review-variant";
  note.setAttribute("aria-label", "Our Why opening comparison");
  const label = document.createElement("span");
  label.textContent = "Alternate opening \xB7 Inset expansion";
  const link = document.createElement("a");
  link.href = "/team/";
  link.textContent = "View default opening";
  note.append(label, link);
  document.body.append(note);
}
var disposers = /* @__PURE__ */ new WeakMap();
var selector = "[data-partner-story],[data-why-story]";
function init(root = document) {
  for (const node of [...root.matches?.(selector) ? [root] : [], ...root.querySelectorAll(selector)]) if (!disposers.has(node)) disposers.set(node, node.matches("[data-why-story]") ? createWhyIntroduction(node) : createPartnerTransitions(node));
}
function dispose(root = document) {
  for (const node of [root, ...root.querySelectorAll(selector)]) {
    disposers.get(node)?.();
    disposers.delete(node);
  }
}
var lifetime = new AbortController();
init();
document.addEventListener("shopify:section:load", (event) => init(event.target), { signal: lifetime.signal });
document.addEventListener("shopify:section:unload", (event) => dispose(event.target), { signal: lifetime.signal });
if (import.meta.hot) import.meta.hot.dispose(() => {
  lifetime.abort();
  dispose();
});
